import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../../api/notification';
import { useNotificationStore } from '../../stores/notificationStore';
import { notificationRequest } from './session';
import { unreadCount } from './model';
export function useNotifications(user, onError) {
  const client = useQueryClient(),
    key = ['notifications', 'list', user.id],
    lock = useRef(false),
    alive = useRef(true);
  const [busy, setBusy] = useState(false);
  const revision = useNotificationStore((s) => s.revision),
    lastRevision = useRef(revision);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const query = useQuery({
    queryKey: key,
    retry: false,
    queryFn: async ({ signal }) => {
      const r = notificationRequest();
      signal.addEventListener('abort', r.cancel, { once: true });
      try {
        const response = await notificationApi.getNotifications(r.options);
        if (!r.current()) throw Error('Session changed');
        useNotificationStore.getState().setUnreadCount(unreadCount(response.data));
        return response.data;
      } finally {
        signal.removeEventListener('abort', r.cancel);
        r.finish();
      }
    },
  });
  useEffect(() => {
    if (lastRevision.current === revision) return;
    lastRevision.current = revision;
    void client.invalidateQueries({ queryKey: ['notifications', 'list', user.id], exact: true });
  }, [revision, user.id, client]);
  const change = async (kind, id) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    const r = notificationRequest();
    try {
      await client.cancelQueries({ queryKey: key, exact: true });
      if (!r.current()) return;
      if (kind === 'read') await notificationApi.markAsRead(id, r.options);
      else if (kind === 'all') await notificationApi.markAllAsRead(r.options);
      else await notificationApi.deleteNotification(id, r.options);
      if (!r.current()) return;
      const items = client.getQueryData(key) || [];
      const updated =
        kind === 'delete'
          ? items.filter((n) => n.id !== id)
          : items.map((n) => (kind === 'all' || n.id === id ? { ...n, isRead: true } : n));
      client.setQueryData(key, updated);
      useNotificationStore.getState().setUnreadCount(unreadCount(updated));
      void client.invalidateQueries({ queryKey: key, exact: true });
    } catch {
      if (r.current()) onError();
    } finally {
      r.finish();
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  return { query, busy, change };
}
